/**
 * Every figure and date in the interface goes through here, so the same amount
 * never appears two ways on two screens.
 */

/**
 * Money as the country prints it: its own symbol, its own number of places. IDR has
 * no minor unit, so a rupiah figure carries no decimals rather than two zeros
 * nobody can pay.
 */
export function money(value: number, symbol: string, decimals = 2): string {
  const places = Math.min(Math.max(decimals, 0), 4)

  return `${symbol} ${value.toLocaleString('en-US', {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  })}`
}

/** A round number for a dashboard tile, where the cents are noise. */
export function compactMoney(value: number, symbol: string): string {
  const abs = Math.abs(value)

  if (abs >= 1_000_000) return `${symbol} ${(value / 1_000_000).toFixed(1)}M`
  if (abs >= 10_000) return `${symbol} ${Math.round(value / 1000)}k`

  return money(value, symbol, 0)
}

export function percent(value: number, places = 1): string {
  return `${value.toFixed(places)}%`
}

/** Matches the generated document, so the screen and the PDF read the same. */
export function date(value: string | null | undefined): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'

  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function shortDate(value: string | null | undefined): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'

  return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
}

/**
 * Date and time together, for a record's own history - created, last changed.
 *
 * Local to the reader, because the question being answered is "when did this happen to
 * me", not "what did the server's clock say".
 */
export function dateTime(value: string | null | undefined): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/** "3 days ago", for a notification list where the exact minute is not the point. */
export function relative(value: string | null | undefined, now = new Date()): string {
  if (!value) return ''

  const then = new Date(value)
  if (Number.isNaN(then.getTime())) return ''

  const seconds = Math.round((now.getTime() - then.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`

  return shortDate(value)
}

/** For an ISO date input, which wants yyyy-MM-dd and nothing else. */
export function isoDate(value: Date = new Date()): string {
  return value.toISOString().slice(0, 10)
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'

  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''

  return (first + last).toUpperCase()
}

/**
 * A field name as a person reads it: `companyName` becomes "Company Name", and a
 * picklist's `businessTypeId` becomes "Business Type" rather than naming the key.
 *
 * Used by the history panel, which reads field names off the audit trail, and by the
 * unsaved-changes prompt, which reads them off the form.
 */
export function fieldLabel(field: string): string {
  return field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/ Id$/, '')
    .replace(/^./, (c) => c.toUpperCase())
}

/** "Name", "Name and Manager", "Name, Manager and Description". */
export function sentenceList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}
